'use client';

/**
 * RelationshipTree Component
 *
 * Collapsible nested list showing entity relationships such as
 * owners, loan participation, and guarantee obligations in a
 * tree with expand/collapse at each level.
 *
 * @see Issue #1198 - Entity Relationship Visualization (Level 2)
 */

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { type ReactElement, type ReactNode, useState } from 'react';
import { GlassChip } from '../GlassChip';
import { GlassIconButton } from '../GlassIconButton';

// ============================================================================
// Types
// ============================================================================

export interface RelationshipTreeNode {
  /** Unique node identifier */
  id: string;
  /** Node type determines the displayed icon */
  type: 'individual' | 'entity' | 'case';
  /** Primary label text */
  label: string;
  /** Secondary label text (e.g., role description) */
  sublabel?: string;
  /** Badge text (e.g., "40%", "Primary Borrower", "$500,000") */
  badge?: string;
  /** Badge color variant */
  badgeColor?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'default';
  /** Navigable link for this node */
  href?: string;
  /** Child nodes rendered recursively */
  children?: RelationshipTreeNode[];
}

export interface RelationshipTreeProps {
  /** Root entity label displayed above sections */
  rootLabel: string;
  /** Tree sections to display */
  sections: Array<{
    title: string;
    icon: ReactNode;
    nodes: RelationshipTreeNode[];
  }>;
  /** Called when a node with href is clicked */
  onNodeClick?: (href: string) => void;
  /** Initially expanded section indices (default: all) */
  defaultExpanded?: number[];
}

// ============================================================================
// Icon Map
// ============================================================================

const nodeTypeIcons: Record<RelationshipTreeNode['type'], ReactElement> = {
  individual: <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />,
  entity: <AccountBalanceIcon sx={{ fontSize: 20, color: 'text.secondary' }} />,
  case: <AssignmentIcon sx={{ fontSize: 20, color: 'text.secondary' }} />,
};

// ============================================================================
// TreeNode (recursive renderer)
// ============================================================================

function TreeNode({
  node,
  depth,
  onNodeClick,
}: {
  node: RelationshipTreeNode;
  depth: number;
  onNodeClick?: (href: string) => void;
}) {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = node.children != null && node.children.length > 0;

  return (
    <>
      <Box
        sx={{
          pl: 3 * depth,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {hasChildren ? (
          <GlassIconButton
            size="small"
            variant="standard"
            onClick={() => setExpanded((prev) => !prev)}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </GlassIconButton>
        ) : (
          <Box sx={{ width: 32 }} />
        )}

        {nodeTypeIcons[node.type]}

        {node.href ? (
          <ButtonBase
            onClick={() => onNodeClick?.(node.href!)}
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              fontSize: '0.875rem',
              textAlign: 'left',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {node.label}
          </ButtonBase>
        ) : (
          <Typography variant="body2">{node.label}</Typography>
        )}

        {node.sublabel && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {node.sublabel}
          </Typography>
        )}

        {node.badge && <GlassChip size="small" label={node.badge} color={node.badgeColor ?? 'default'} />}
      </Box>
      {hasChildren &&
        expanded &&
        node.children!.map((child) => (
          <TreeNode key={child.id} node={child} depth={depth + 1} onNodeClick={onNodeClick} />
        ))}
    </>
  );
}

// ============================================================================
// RelationshipTree
// ============================================================================

export function RelationshipTree({ rootLabel, sections, onNodeClick, defaultExpanded }: RelationshipTreeProps) {
  // By default all sections are expanded
  const initialExpanded = defaultExpanded ?? sections.map((_, idx) => idx);

  const [expandedSections, setExpandedSections] = useState<number[]>(initialExpanded);

  const handleToggleSection = (index: number) => {
    setExpandedSections((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ px: 2, pt: 1, pb: 0.5, color: 'text.secondary' }}>
        {rootLabel}
      </Typography>

      {sections.map((section, idx) => (
        <Accordion
          key={section.title}
          expanded={expandedSections.includes(idx)}
          onChange={() => handleToggleSection(idx)}
          disableGutters
          elevation={0}
          sx={{
            backgroundColor: 'transparent',
            '&::before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              minHeight: 40,
              '& .MuiAccordionSummary-content': {
                my: 0.5,
                alignItems: 'center',
                gap: 1,
              },
            }}
          >
            {section.icon}
            <Typography variant="subtitle2">{section.title}</Typography>
            <GlassChip size="small" label={String(section.nodes.length)} color="default" sx={{ ml: 'auto' }} />
          </AccordionSummary>

          <AccordionDetails sx={{ px: 1, pt: 0 }}>
            {section.nodes.map((node) => (
              <TreeNode key={node.id} node={node} depth={0} onNodeClick={onNodeClick} />
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

RelationshipTree.displayName = 'RelationshipTree';

export default RelationshipTree;
